import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import Modal from "@/components/Modal";

function ProductTypeForm({ initial, onSubmit, onCancel }) {
  const { t } = useI18n();
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  // Sprint 3 — Conditional fields editor. Each row: {key, label, type, unit,
  // options(string CSV), required}. We keep options as a comma-separated
  // string during editing for UX; convert to array on submit.
  const [fields, setFields] = useState(() =>
    (initial?.field_schema || []).map((f) => ({
      key: f.key || "",
      label: f.label || "",
      type: f.type || "text",
      unit: f.unit || "",
      options: (f.options || []).join(", "),
      required: !!f.required,
    }))
  );
  const [submitting, setSubmitting] = useState(false);

  const updateField = (idx, patch) => {
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const addField = () => {
    setFields((prev) => [
      ...prev,
      { key: "", label: "", type: "text", unit: "", options: "", required: false },
    ]);
  };
  const removeField = (idx) => {
    setFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const handle = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(t("name_required") || t("product_type_name"));
      return;
    }
    // Client-side schema validation
    const seenKeys = new Set();
    const keyRe = /^[a-z][a-z0-9_]{0,40}$/;
    const fieldSchema = [];
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      const key = (f.key || "").trim().toLowerCase();
      const label = (f.label || "").trim();
      if (!key || !keyRe.test(key)) {
        toast.error(`${t("field_key")} #${i + 1}: ${t("field_key_hint")}`);
        return;
      }
      if (seenKeys.has(key)) {
        toast.error(`${t("field_key")} duplicada: ${key}`);
        return;
      }
      seenKeys.add(key);
      if (!label) {
        toast.error(`${t("field_label")} requerida (#${i + 1})`);
        return;
      }
      const opts = (f.options || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (f.type === "select" && opts.length === 0) {
        toast.error(`${t("field_options")} requeridas para "${label}"`);
        return;
      }
      fieldSchema.push({
        key,
        label,
        type: f.type,
        options: opts,
        unit: (f.unit || "").trim() || null,
        required: !!f.required,
        order: i,
      });
    }

    setSubmitting(true);
    try {
      await onSubmit({
        name: trimmed,
        description: description.trim(),
        field_schema: fieldSchema,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handle} className="space-y-5">
      <div>
        <label className="label-overline block mb-1">{t("product_type_name")}</label>
        <input
          data-testid={DASHBOARD.productTypeFormName}
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border border-[var(--border)] px-3 py-2 font-display"
          placeholder={t("product_type_name")}
        />
      </div>
      <div>
        <label className="label-overline block mb-1">{t("product_type_description")}</label>
        <textarea
          data-testid={DASHBOARD.productTypeFormDescription}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full border border-[var(--border)] px-3 py-2 font-display resize-y"
          placeholder={t("product_type_description")}
        />
      </div>

      {/* --- Field schema editor (Sprint 3) --- */}
      <div className="border-t border-[var(--border)] pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-overline">{t("field_schema_section")}</div>
            <div className="text-xs text-[var(--text-secondary)] mt-0.5">
              {t("field_key_hint")}
            </div>
          </div>
          <button
            type="button"
            data-testid={DASHBOARD.productTypeSchemaAddBtn}
            onClick={addField}
            className="border border-[var(--border)] px-3 py-1.5 text-xs font-mono-tech hover:bg-[var(--bg)]"
          >
            {t("add_field")}
          </button>
        </div>

        {fields.length === 0 ? (
          <div className="text-xs text-[var(--text-secondary)] italic">—</div>
        ) : (
          <div className="space-y-3">
            {fields.map((f, idx) => (
              <div
                key={idx}
                data-testid={DASHBOARD.productTypeSchemaRow(idx)}
                className="grid grid-cols-12 gap-2 items-start border border-[var(--border)] p-3 bg-[var(--bg)]"
              >
                <div className="col-span-12 sm:col-span-3">
                  <div className="text-[0.65rem] font-mono-tech text-[var(--text-secondary)] mb-1">
                    {t("field_key")}
                  </div>
                  <input
                    data-testid={DASHBOARD.productTypeSchemaKey(idx)}
                    value={f.key}
                    onChange={(e) => updateField(idx, { key: e.target.value })}
                    placeholder="width"
                    className="w-full border border-[var(--border)] px-2 py-1.5 text-sm font-mono-tech bg-white"
                  />
                </div>
                <div className="col-span-12 sm:col-span-3">
                  <div className="text-[0.65rem] font-mono-tech text-[var(--text-secondary)] mb-1">
                    {t("field_label")}
                  </div>
                  <input
                    data-testid={DASHBOARD.productTypeSchemaLabel(idx)}
                    value={f.label}
                    onChange={(e) => updateField(idx, { label: e.target.value })}
                    placeholder="Ancho"
                    className="w-full border border-[var(--border)] px-2 py-1.5 text-sm bg-white"
                  />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <div className="text-[0.65rem] font-mono-tech text-[var(--text-secondary)] mb-1">
                    {t("field_type")}
                  </div>
                  <select
                    data-testid={DASHBOARD.productTypeSchemaType(idx)}
                    value={f.type}
                    onChange={(e) => updateField(idx, { type: e.target.value })}
                    className="w-full border border-[var(--border)] px-2 py-1.5 text-sm bg-white"
                  >
                    <option value="text">{t("field_type_text")}</option>
                    <option value="number">{t("field_type_number")}</option>
                    <option value="select">{t("field_type_select")}</option>
                  </select>
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <div className="text-[0.65rem] font-mono-tech text-[var(--text-secondary)] mb-1">
                    {f.type === "select" ? t("field_options") : t("field_unit")}
                  </div>
                  {f.type === "select" ? (
                    <input
                      data-testid={DASHBOARD.productTypeSchemaOptions(idx)}
                      value={f.options}
                      onChange={(e) => updateField(idx, { options: e.target.value })}
                      placeholder="cm, m, pulg"
                      className="w-full border border-[var(--border)] px-2 py-1.5 text-sm bg-white"
                    />
                  ) : (
                    <input
                      data-testid={DASHBOARD.productTypeSchemaUnit(idx)}
                      value={f.unit}
                      onChange={(e) => updateField(idx, { unit: e.target.value })}
                      placeholder="cm"
                      className="w-full border border-[var(--border)] px-2 py-1.5 text-sm bg-white"
                    />
                  )}
                </div>
                <div className="col-span-6 sm:col-span-1 flex flex-col items-center justify-center pt-4">
                  <label className="text-[0.6rem] font-mono-tech text-[var(--text-secondary)] flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={DASHBOARD.productTypeSchemaRequired(idx)}
                      checked={f.required}
                      onChange={(e) => updateField(idx, { required: e.target.checked })}
                    />
                    REQ
                  </label>
                </div>
                <div className="col-span-6 sm:col-span-1 flex items-center justify-end pt-4">
                  <button
                    type="button"
                    data-testid={DASHBOARD.productTypeSchemaRemove(idx)}
                    onClick={() => removeField(idx)}
                    className="text-xs text-[var(--alert-overdue)] hover:underline"
                    title={t("remove_field")}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          data-testid={DASHBOARD.productTypeFormCancel}
          onClick={onCancel}
          className="border border-[var(--border)] px-4 py-2 text-sm hover:bg-[var(--bg)]"
        >
          {t("cancel")}
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid={DASHBOARD.productTypeFormSubmit}
          className="bg-[var(--text-primary)] text-white px-5 py-2 font-display font-bold tracking-wide hover:bg-[var(--primary)] disabled:opacity-50"
        >
          {t("save")}
        </button>
      </div>
    </form>
  );
}

/**
 * Product Types Panel — admin-only.
 * Sprint 2 (Plan Maestro Rawens · Etapa 1). Strictly additive infrastructure:
 * defines product TYPES only — NOT connected to products, orders, costs,
 * profit or workflows yet. Future sprints will wire products to a type.
 */
export default function ProductTypesPanel({ onBack }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/product-types`, { withCredentials: true });
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (payload) => {
    try {
      if (editing?.id) {
        await axios.put(`${API}/product-types/${editing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/product-types`, payload, { withCredentials: true });
      }
      toast.success(t("saved_ok"));
      setOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const toggle = async (pt) => {
    try {
      await axios.patch(`${API}/product-types/${pt.id}/toggle-active`, null, { withCredentials: true });
      toast.success(t("state_changed_ok"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const filtered = items.filter((p) => {
    if (statusFilter === "active") return p.is_active;
    if (statusFilter === "inactive") return !p.is_active;
    return true;
  });

  return (
    <div data-testid={DASHBOARD.productTypesRoot} className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button
            data-testid={DASHBOARD.productTypesBackBtn}
            onClick={onBack}
            className="font-mono-tech text-xs text-[var(--text-secondary)] hover:text-black mb-2"
          >
            {t("back_to_settings")}
          </button>
          <div className="label-overline mb-1">SET / 02</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
            {t("product_types_title")}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {t("product_types_subtitle")}
          </p>
        </div>
      </div>

      <div data-testid={DASHBOARD.productTypesList} className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <label className="label-overline block mb-1">{t("status")}</label>
            <select
              data-testid={DASHBOARD.productTypeFilterStatus}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-[var(--border)] px-3 py-2 font-mono-tech text-sm bg-white"
            >
              <option value="all">{t("all_statuses")}</option>
              <option value="active">{t("active_only")}</option>
              <option value="inactive">{t("inactive_only")}</option>
            </select>
          </div>
          <button
            data-testid={DASHBOARD.productTypeNewBtn}
            onClick={() => {
              setEditing({});
              setOpen(true);
            }}
            className="bg-[var(--text-primary)] text-white px-5 py-2.5 font-display font-bold tracking-wide hover:bg-[var(--primary)] transition-colors"
          >
            + {t("new_product_type")}
          </button>
        </div>

        {loading ? (
          <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
            LOADING…
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-[var(--border)] bg-white py-16 text-center text-[var(--text-secondary)]">
            {t("no_product_types")}
          </div>
        ) : (
          <div className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]">
            {filtered.map((p) => (
              <div
                key={p.id}
                data-testid={DASHBOARD.productTypeRow(p.id)}
                className={`grid grid-cols-12 gap-4 items-start px-5 py-4 hover:bg-[var(--bg)] ${
                  p.is_active ? "" : "opacity-60"
                }`}
              >
                <div className="col-span-12 md:col-span-7">
                  <div className="font-display text-lg font-bold tracking-tight">{p.name}</div>
                  {p.description ? (
                    <div className="text-xs text-[var(--text-secondary)] mt-1 whitespace-pre-wrap">
                      {p.description}
                    </div>
                  ) : null}
                  {p.field_schema && p.field_schema.length > 0 ? (
                    <div className="text-[0.65rem] font-mono-tech text-[var(--text-secondary)] mt-1.5 tracking-wide">
                      {p.field_schema.length} CAMPOS · {p.field_schema.map((f) => f.label).join(" · ")}
                    </div>
                  ) : null}
                </div>
                <div className="col-span-6 md:col-span-2">
                  <span
                    className={`font-mono-tech text-[0.65rem] tracking-wider px-2 py-1 ${
                      p.is_active
                        ? "bg-black text-white"
                        : "border border-[var(--border)] text-[var(--text-secondary)]"
                    }`}
                  >
                    {p.is_active ? t("active").toUpperCase() : t("inactive").toUpperCase()}
                  </span>
                </div>
                <div className="col-span-6 md:col-span-3 flex justify-end gap-2">
                  <button
                    data-testid={DASHBOARD.productTypeEditBtn(p.id)}
                    onClick={() => {
                      setEditing(p);
                      setOpen(true);
                    }}
                    className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
                  >
                    {t("edit")}
                  </button>
                  <button
                    data-testid={DASHBOARD.productTypeToggleBtn(p.id)}
                    onClick={() => toggle(p)}
                    className={`border px-3 py-1.5 text-sm transition-colors ${
                      p.is_active
                        ? "border-[var(--border)] hover:bg-[var(--bg)]"
                        : "border-black bg-black text-white hover:opacity-80"
                    }`}
                  >
                    {p.is_active ? t("deactivate") : t("activate")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        title={editing?.id ? t("edit_product_type") : t("new_product_type")}
        testId="product-type-modal"
        wide
      >
        <ProductTypeForm
          initial={editing}
          onSubmit={submit}
          onCancel={() => {
            setOpen(false);
            setEditing(null);
          }}
        />
      </Modal>
    </div>
  );
}
